/**
 * The Assist card, waitlist variant: the view only (no data, no API).
 *
 * Board Listing.dc.html, Tweak "Before registration (waitlist)"; screens
 * listing-assist-waitlist.png (idle) and listing-assist-waitlist-joined.png.
 * Styles: components/assist/assist.css, imported by the container
 * (AssistCard.tsx) so this file stays importable by `tsx` tests.
 *
 * Waitlist mode shows no paid service (brief R2): no fee, no percentage, no
 * link to /assist, the request sheet or a Purchase Room.
 */
import { useId } from 'react';
import AssistMark from './AssistMark';
import { cardCopy, commonCopy } from '../../src/lib/assist/copy';

export type AssistWaitlistState = 'idle' | 'submitting' | 'joined';

export interface AssistWaitlistCardProps {
  state: AssistWaitlistState;
  onJoin: () => void;
  /** Shown under the button when the last attempt failed. */
  errorMessage?: string | null;
  /** Animate the joined box in (only when it appears during this visit). */
  animateJoined?: boolean;
}

const copy = cardCopy.waitlist;

export default function AssistWaitlistCard({
  state,
  onJoin,
  errorMessage,
  animateJoined = false,
}: AssistWaitlistCardProps) {
  const headingId = useId();
  const joined = state === 'joined';
  const submitting = state === 'submitting';

  return (
    <section
      className="assist-scope aq-card"
      aria-labelledby={headingId}
      data-assist-card="waitlist"
    >
      <p className="aq-brand">
        <AssistMark variant="onMoss" height={15} />
        {commonCopy.brandName}
      </p>

      <h2 id={headingId} className="aq-headline">
        {copy.headlineLead}
        <span className="aq-headline-follow">{copy.headlineFollow}</span>
      </h2>

      <p className="aq-body">{copy.body}</p>

      {!joined && (
        <>
          <button
            type="button"
            className="aq-button"
            onClick={onJoin}
            disabled={submitting}
            aria-disabled={submitting || undefined}
            aria-busy={submitting || undefined}
          >
            {copy.button}
          </button>
          <span className="aq-footnote">{copy.footnote}</span>
        </>
      )}

      {/* Always present, so assistive technology announces the change. */}
      <div role="status" className="aq-status">
        {joined && (
          <div className={`aq-joined${animateJoined ? ' aq-joined--enter' : ''}`}>
            <span className="aq-joined-icon" aria-hidden="true">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                focusable="false"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            <div style={{ minWidth: 0 }}>
              <p className="aq-joined-title">{copy.joinedTitle}</p>
              <p className="aq-joined-body">{copy.joinedBody}</p>
            </div>
          </div>
        )}
      </div>

      {errorMessage && !joined && (
        <p className="aq-error" role="alert">
          {errorMessage}
        </p>
      )}
    </section>
  );
}
